import { LoopReducer, loop, Loop, Cmd } from "redux-loop";
import {
  getType,
  createAsyncAction,
  ActionType,
  createAction,
} from "typesafe-actions";
import eventService from "../../services/events";
import {
  eventObject,
  ListEventsState,
  lyytiEventObject,
  subEventObject,
} from "../../types/types";
import ConsoleHelper from "../../utils/logger";

const initialState: ListEventsState = {
  initLoading: false,
  eventsFetched: false,
  events: [],
  lyytiEvents: [],
};

export const getAllEventsAsync = createAsyncAction(
  "START_EVENTS_FETCH",
  "EVENTS_FETCH_COMPLETE",
  "EVENTS_FETCH_FAIL"
)<
  { date: string; limit: string },
  { data: eventObject[]; status: number },
  Error
>();

export const getAllLyytiEventsAsync = createAsyncAction(
  "START_LYYTIEVENTS_FETCH",
  "LYYTIEVENTS_FETCH_COMPLETE",
  "LYYTIEVENTS_FETCH_FAIL"
)<string, { data: any; status: number; locale: string }, Error>();

export const getAllLyytiParticipantsAsync = createAsyncAction(
  "START_LYYTIPARTICIPANTS_FETCH",
  "LYYTIPARTICIPANTS_FETCH_COMPLETE",
  "LYYTIPARTICIPANTS_FETCH_FAIL"
)<string, { data: any; status: number }, Error>();

const getEventDetails = createAction("GET_EVENT_DETAILS")<{
  id: string;
  subEventId?: string;
}>();

type Action =
  | ActionType<typeof getAllEventsAsync>
  | ActionType<typeof getAllLyytiEventsAsync>
  | ActionType<typeof getAllLyytiParticipantsAsync>
  | ActionType<typeof getEventDetails>;

export const listEventsReducer: LoopReducer<ListEventsState, Action> = (
  state = initialState,
  action: Action
): ListEventsState | Loop<ListEventsState> => {
  switch (action.type) {
    //get all events
    case getType(getAllEventsAsync.request):
      return loop(
        { ...state, initLoading: true },
        Cmd.run(eventService.getAll, {
          successActionCreator: getAllEventsAsync.success,
          failActionCreator: getAllEventsAsync.failure,
          args: [action.payload],
        })
      );
    case getType(getAllEventsAsync.failure):
      return { ...state, initLoading: false };

    case getType(getAllEventsAsync.success):
      ConsoleHelper(action.payload);
      if (action.payload.status === 200) {
        return {
          ...state,
          initLoading: false,
          eventsFetched: true,
          events: action.payload.data,
        };
      } else {
        ConsoleHelper("could not get all events");
        ConsoleHelper(action.payload.status);
        return { ...state, initLoading: false };
      }

    //get all LyytiEvents
    case getType(getAllLyytiEventsAsync.request):
      return loop(
        { ...state, initLoading: true },
        Cmd.run(eventService.getEventsLyyti, {
          successActionCreator: getAllLyytiEventsAsync.success,
          failActionCreator: getAllLyytiEventsAsync.failure,
          args: [action.payload],
        })
      );
    case getType(getAllLyytiEventsAsync.failure):
      return { ...state, initLoading: false };

    case getType(getAllLyytiEventsAsync.success):
      ConsoleHelper(action.payload);
      if (action.payload.status === 200) {
        ConsoleHelper(
          "here are uuids for lyytievents:",
          Object.keys(action.payload.data.results)
        );
        const lyytiEvents: lyytiEventObject[] = Object.keys(
          action.payload.data.results
        ).map((lyytiEvent) =>
          mapToLyytiEventObject(
            action.payload.data.results[lyytiEvent],
            action.payload.locale
          )
        );
        return {
          ...state,
          initLoading: false,
          lyytiEvents: lyytiEvents,
        };
      } else {
        ConsoleHelper("could not get all lyytievents");
        ConsoleHelper(action.payload.status);
        return { ...state, initLoading: false };
      }

    //get all LyytiEvents
    case getType(getAllLyytiParticipantsAsync.request):
      if (action.payload.length === 0) {
        return state;
      }
      const event = state.lyytiEvents.find(
        (event) => event.name === action.payload
      );
      return loop(
        { ...state, initLoading: true },
        Cmd.run(eventService.getParticipantsLyyti, {
          successActionCreator: getAllLyytiParticipantsAsync.success,
          failActionCreator: getAllLyytiParticipantsAsync.failure,
          args: [event.eid],
        })
      );
    case getType(getAllLyytiParticipantsAsync.failure):
      return { ...state, initLoading: false };

    case getType(getAllLyytiParticipantsAsync.success):
      ConsoleHelper(action.payload);
      if (action.payload.status === 200) {
        return {
          ...state,
          initLoading: false,
        };
      } else {
        ConsoleHelper("could not get all lyyti participants");
        ConsoleHelper(action.payload.status);
        return { ...state, initLoading: false };
      }

    //set event details
    case getType(getEventDetails):
      const a = state.events.find((e) => e.eventId === action.payload.id);
      const details = {
        eventName: a.eventName,
        eventPlace: a.eventPlace,
        eventStreetAddress: a.eventStreetAddress,
        eventZip: a.eventZip,
        eventCity: a.eventCity,
        eventCountry: a.eventCountry,
        eventStartDate: `${a.startDate}T${a.startDateTime}`,
        eventEndDate: `${a.endDate}T${a.endDateTime}`,
        eventDescription: a.description,
        supportEmail: a.eventSupportEmail,
        supportPhone: a.eventSupportPhone,
        supportInstruction: a.eventSupportInstructrion,
      };

      const payload = action.payload.subEventId
        ? { details, subEventId: action.payload.subEventId }
        : { details };

      return loop(state, Cmd.action({ type: "SET_EVENT_DETAILS", payload }));
    default:
      return state;
  }
};

export function getListEventsState(state): ListEventsState {
  return state.listEventsState;
}

export type SubEventsTableData = {
  list: subEventObject[];
  headerRow: string[];
  initLoading: boolean;
};
export function selectSubEvents(state, eventId): SubEventsTableData {
  const initLoading = checkLoading([
    state.listEventsState.initLoading,
    state.eventState.initLoading,
  ]);
  const found = state.listEventsState.events.find((e) => e.eventId === eventId);
  const list = found ? found.subEvents : [];
  const headerRow =
    list.length > 0
      ? Object.keys(list[0]).length > 5
        ? Object.keys(list[0]).slice(0, 5)
        : Object.keys(list[0])
      : [];
  return { list, headerRow, initLoading };
}

export function checkLoading(args: boolean[]): boolean {
  return args.find((boolean) => boolean);
}

export function mapToLyytiEventObject(
  lyytiEvent: any,
  locale: string
): lyytiEventObject {
  const contact_person = lyytiEvent.contact_person[locale]
    ? lyytiEvent.contact_person[locale]
    : Object.values(lyytiEvent.contact_person)[0];
  return {
    eid: lyytiEvent.eid,
    owner: lyytiEvent.owner,
    owner_name: lyytiEvent.owner_name,
    name: lyytiEvent.name[locale]
      ? lyytiEvent.name[locale]
      : Object.values(lyytiEvent.name)[0],
    location: lyytiEvent.location[locale]
      ? lyytiEvent.location[locale]
      : Object.values(lyytiEvent.location)[0],
    address: lyytiEvent.address[locale]
      ? lyytiEvent.address[locale]
      : Object.values(lyytiEvent.address)[0],
    homepage: lyytiEvent.homepage[locale]
      ? lyytiEvent.homepage[locale]
      : Object.values(lyytiEvent.homepage)[0],
    homepage_title: lyytiEvent.homepage_title[locale]
      ? lyytiEvent.homepage_title[locale]
      : Object.values(lyytiEvent.homepage_title)[0],
    participant_type_heading: lyytiEvent.participant_type_heading[locale]
      ? lyytiEvent.participant_type_heading[locale]
      : Object.values(lyytiEvent.participant_type_heading)[0],
    online_event_link_title: lyytiEvent.online_event_link_title[locale]
      ? lyytiEvent.online_event_link_title[locale]
      : Object.values(lyytiEvent.online_event_link_title)[0],
    contact_person_name: contact_person.name,
    contact_person_email: contact_person.email,
    has_participants: lyytiEvent.hasParticipants,
  };
}
